import React from 'react'
import * as Yup from 'yup'
import { Button } from '../../../components/common/Buttons'
import { useTrans } from '../../../services/i18n'
import AuthLayout from '../../../components/layouts/AuthLayout'
import {
  AUTH_NEW_PASSWORD_REQUIRED,
  AUTH_SMS_MFA,
  AUTH_SOFTWARE_TOKEN_MFA,
  ROUTE_SIGNUP,
} from '../../../constants'
import { getPostAuthRedirectPath, isMfaConfigured } from '../../../utils/mfa'
import { markAdminMfaSessionSatisfied } from '../../../utils/adminMfaSession'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import {
  AmplifyAuth,
  getAmplifyAuthData,
  forgetCurrentDevice,
  rememberCurrentDevice,
} from '../../../services/amplifyAuth'
import { hasDeviceTrust, isDeviceTrustExpired } from '../../../utils/mfaDeviceTrust'
import { useApplication } from '../../../services/application'
import SignInForm from './components/SignInForm'
import makeValidationSchema from '../../../hooks/makeValidationSchema'
import Authentication from './components/Authentication'
import useErrorState from '../../../hooks/useErrorState'
import ChangePasswordForm from '../ChangePassword/ChangePasswordForm'
import {
  shouldResendExceptions,
  temporaryPasswordExpired,
  userNotConfirmed,
} from './constants'
import useLogout from '../../../hooks/useLogout'
import {
  ADMIN_LOGOUT_REASON_KEY,
  INACTIVITY_LOGOUT_REASON,
} from '../../../hooks/useInactivityLogout'
import { ANNOUNCEMENT_BANNER_DISMISSED_KEY } from '../../../utils/announcementBannerUtils'

const useScheme = makeValidationSchema(trans => {
  return Yup.object().shape({
    login: Yup.string()
      .email(trans('enter-valid-email-password'))
      .required(trans('enter-valid-email-password')),
    password: Yup.string().required(trans('enter-valid-email-password')),
  })
})

const SignIn = () => {
  const { trans } = useTrans()
  const navigate = useNavigate()
  const application = useApplication()
  const schema = useScheme()

  const [error, onError, setError] = useErrorState()
  const [mfaError, setMfaError] = React.useState(undefined)
  const [pending, setPending] = React.useState(false)
  const [user, setAuthUser] = React.useState(undefined)
  const [challenge, setChallenge] = React.useState(undefined)
  const logout = useLogout()

  React.useEffect(() => {
    const logoutReason = sessionStorage.getItem(ADMIN_LOGOUT_REASON_KEY)
    if (logoutReason === INACTIVITY_LOGOUT_REASON) {
      setError('You have been logged out due to inactivity.')
      sessionStorage.removeItem(ADMIN_LOGOUT_REASON_KEY)
    }
  }, [setError])

  const loginToApplication = React.useCallback(
    async user => {
      await application.authorize(getAmplifyAuthData(user))

      if (isMfaConfigured(application.auth)) {
        markAdminMfaSessionSatisfied(application.auth.username)
      }

      sessionStorage.removeItem(ANNOUNCEMENT_BANNER_DISMISSED_KEY)
      navigate(getPostAuthRedirectPath(application.auth))
    },
    [application, navigate]
  )

  const signInUser = React.useCallback(
    async ({ login, password }) => {
      try {
        setError(undefined)
        const user = await AmplifyAuth.signIn(login.toLowerCase(), password)
        if (user) {
          const { apiKey } = await application.call('admin.createApiKey', {}, false)
          if (apiKey) {
            const checkUser = await application.callPost('admin.checkUser', { email: login }, { API_KEY: apiKey })
            if (!checkUser || !checkUser.success) {
              logout()
              return Promise.reject(trans('incorrect-username-password'))
            }
          }
        }

        if (user.challengeName === AUTH_NEW_PASSWORD_REQUIRED) {
          setAuthUser(user)
          return setChallenge(AUTH_NEW_PASSWORD_REQUIRED)
        }

        if (
          user.challengeName === AUTH_SMS_MFA ||
          user.challengeName === AUTH_SOFTWARE_TOKEN_MFA
        ) {
          setError(undefined)
          setMfaError(undefined)
          setAuthUser(user)
          return setChallenge(user.challengeName)
        }

        // No MFA challenge means either the account has no MFA, or this is a
        // Cognito "remembered device" that bypassed it. If a remembered device
        // has passed its 30-day window, drop the trust so the next sign-in
        // requires MFA again (Cognito has no native expiry for this).
        if (hasDeviceTrust() && isDeviceTrustExpired()) {
          await forgetCurrentDevice()
        }

        return loginToApplication(user)
      } catch (e) {
        if (shouldResendExceptions.includes(e.code)) {
          return Promise.reject(userNotConfirmed)
        }
        if (
          e.code === 'NotAuthorizedException' &&
          e.message
            ?.toLowerCase()
            .includes(
              'temporary password has expired and must be reset by an administrator'
            )
        ) {
          return Promise.reject(temporaryPasswordExpired)
        }
        return Promise.reject(trans('incorrect-username-password'))
      }
    },
    [loginToApplication, trans]
  )

  const onSubmit = React.useCallback(
    values => {
      setPending(true)
      schema
        .validate(values)
        .then(signInUser)
        .catch(e => {
          if (e === temporaryPasswordExpired) {
            setError(temporaryPasswordExpired)
            return
          }
          if (e === userNotConfirmed) {
            setError(userNotConfirmed)
            return
          }
          return onError(e)
        })
        .finally(() => setPending(false))
    },
    [onError, schema, signInUser]
  )

  const onMfaBack = React.useCallback(() => {
    setAuthUser(undefined)
    setChallenge(undefined)
    setMfaError(undefined)
    setPending(false)
  }, [])

  const onMfaCancel = React.useCallback(
    error => {
      if (error) onError(error)
      setAuthUser(undefined)
      setChallenge(undefined)
      setPending(false)
    },
    [onError]
  )

  const onMfaSubmit = React.useCallback(
    async (code, rememberDevice) => {
      const authUser = user
      if (!authUser || !challenge) {
        return
      }

      setPending(true)
      setMfaError(undefined)

      try {
        const signedInUser = await AmplifyAuth.confirmSignIn(authUser, code, challenge)

        try {
          if (rememberDevice) {
            await rememberCurrentDevice()
          } else {
            await forgetCurrentDevice()
          }
        } catch (e) {
          // non-fatal: user is still authenticated for this session
        }

        await loginToApplication(signedInUser)
      } catch (e) {
        setMfaError(trans('incorrect-authentication-code'))
        setPending(false)
      }
    },
    [user, challenge, loginToApplication, trans]
  )

  const onNewPasswordSubmit = React.useCallback(
    ({ password }) => {
      return setAuthUser(authUser => {
        AmplifyAuth.completeNewPassword(authUser, password)
          .then(loginToApplication)
          .then(() => {
            if (
              authUser.challengeParam.userAttributes.email_verified === 'false'
            ) {
              return application.call('organization.updateOrgUserStatus')
            }
          })
          .catch(() => onError(trans('incorrect-password'))) // Shout it be translated here?
          .finally(onMfaCancel)

        return undefined
      })
    },
    [application, loginToApplication, onError, onMfaCancel, trans]
  )

  return (
    <AuthLayout
      actions={
        <Button
          id="sign-up-btn"
          color="secondary"
          variant="contained"
          component={RouterLink}
          to={ROUTE_SIGNUP.path}
        >
          {trans('sign-up')}
        </Button>
      }
    >
      {[AUTH_SOFTWARE_TOKEN_MFA, AUTH_SMS_MFA].includes(challenge) ? (
        <Authentication
          challenge={challenge}
          onSubmit={onMfaSubmit}
          onCancel={onMfaBack}
          user={user}
          pending={pending}
          error={mfaError}
          onClearError={() => setMfaError(undefined)}
        />
      ) : challenge === AUTH_NEW_PASSWORD_REQUIRED ? (
        <ChangePasswordForm onSubmit={onNewPasswordSubmit} />
      ) : (
        <SignInForm
          onSubmit={onSubmit}
          pending={pending}
          error={error}
          onFlushError={() => {
            setError(undefined)
          }}
        />
      )}
    </AuthLayout>
  )
}

export default SignIn